import * as React from 'react';
import { type ReactNode, useEffect, useMemo } from 'react';

import type { Analytics, TrackLink } from 'src/modules/analytics.types';
import { tagRecording } from 'src/modules/hotjar';

const TrackingContext = React.createContext<Analytics | null>(null);

TrackingContext.displayName = 'TrackingContext';

interface TrackingProviderProps {
  children: ReactNode;
  client: Analytics;
}

const createTrackLink =
  (client: Analytics): TrackLink =>
  ({ link, ...rest }) => {
    client.trackLink({ link, ...rest });
    tagRecording(link);
  };

export const TrackingProvider = ({
  children,
  client,
}: TrackingProviderProps) => (
  <TrackingContext.Provider value={client}>{children}</TrackingContext.Provider>
);

export const useTrackingClient = (props: Record<string, any> = {}) => {
  const trackingClient = React.useContext(TrackingContext);
  if (!trackingClient) {
    throw new Error('useTrackingClient must be used within a TrackingProvider');
  }

  useEffect(() => {
    // TODO: fix typing, probably use zod for runtime validation
    // @ts-expect-error
    trackingClient.updateAnalyticsState(props);
  }, [trackingClient, props]);

  return useMemo(
    () => ({
      trackLink: createTrackLink(trackingClient),
      trackView: trackingClient.trackView,
      setPage: trackingClient.setPage,
      trackingCorrelationId: trackingClient.trackingCorrelationId,
    }),
    [trackingClient],
  );
};

export enum Page {
  DOCUMENT_PAGE = 'apply documents',
  ROLE_REQUIREMENTS = 'apply questions',
  PROFILE_PAGE = 'apply profile',
  REVIEW_PAGE = 'apply review',
  APPLICATION_COMPLETE = 'apply complete',
  APPLY_LINKOUT = 'apply linkout',
  APPLY_LINKOUT_COMPLETE = 'apply linkout complete',
}

export enum ActionOrigin {
  APPLY = 'steps:applicationForm',
}

export enum UpdateType {
  ADD = 'add',
  EDIT = 'edit',
  DELETE = 'delete',
}

export enum PageSectionState {
  ADD = 'add',
  EDIT = 'edit',
}

export enum PageSubSection {
  VISIBILITY = 'visibility',
}

export enum PageSection {
  APPLY_COMPLETE = 'apply complete',
  APPLY_LINKOUT_COMPLETE = 'apply linkout complete',
  DOCUMENTS = 'documents',
  PROFILE = 'profile',
  EMPLOYER_QUESTIONS = 'employer questions',
  REVIEW_APPLICATION = 'review application',
  JOB_DETAILS = 'job details',
  COVER_LETTER = 'cover letter',
  REVIEW_FEEDBACK = 'review feedback',
  RESUME = 'resume',
  SELECTION_CRITERIA = 'selection criteria',
  TEXT_EDITOR = 'text editor',
  RECENT_ROLE = 'recent role',
  CAREER_HISTORY = 'career history',
  CERTSY = 'certsy',
  EDUCATION = 'education',
  LICENCES = 'licences',
  SKILLS = 'skills',
  PERSONAL_DETAILS = 'personal details',
  PROFILE_VISIBILITY = 'profile visibility',
  REFERENCES = 'references',
}

export enum ReferencesSelection {
  INCLUDE = 'Include references',
  DONT_INCLUDE = 'Don’t include references',
}

// TODO: Move sign-in, document page, role requirements and profile page (profile-apply)
